import React, { useState, useEffect, useRef, forwardRef } from 'react';
import classNames from 'classnames';
import debounce from 'debounce';
import { filter } from 'ramda';
import caretIconPath from '@icons/arrow.svg';
import searchIconPath from '@icons/search.svg';

import ImageCheckbox from '../imageCheckbox/ImageCheckbox';
import InputField from '../inputField/InputField';
import Loader from '../loader/Loader';
import TagList from '../tagList/TagList';

import { OPTIONS_LENGTH_LIMIT } from '../../js/_constants';
import { useTranslation } from 'react-i18next';
import { isNotEmpty, propNotEq } from '../../js/fpUtils';
import { type CountryChip } from '../../js/dealershipGroupLabel';

import './MultiSelectDropdown.scss';

export type DropdownOptionCountryGroup = {
    prefix: string;
    countries: CountryChip[];
    overflowCountries: CountryChip[];
};

type MultiSelectItemProps = {
    label: string;
    selected: boolean;
    onClick: () => void;
    isLoading?: boolean;
    disabled?: boolean;
    countryGroup?: DropdownOptionCountryGroup;
};

export const MultiSelectItem = forwardRef<HTMLDivElement, MultiSelectItemProps>(
    ({ label, selected, onClick, isLoading, disabled, countryGroup }, ref) => {
        const { t } = useTranslation();
        const [isExpanded, setIsExpanded] = useState(false);
        const hasOverflow = Boolean(countryGroup?.overflowCountries.length);

        const toggleExpanded = (event: React.MouseEvent) => {
            event.stopPropagation();
            setIsExpanded(previous => !previous);
        };

        return (
            <div
                ref={ref}
                key={label}
                className={classNames("b-multi-select-field-item", { "is-disabled": disabled || isLoading })}
                onClick={onClick}
                data-testid={label}
            >
                {countryGroup ? (
                    <div className="b-multi-select-field-item__country-group">
                        <span className="b-multi-select-field-item__country-prefix">
                            {countryGroup.prefix}
                        </span>
                        {(isExpanded
                            ? [...countryGroup.countries, ...countryGroup.overflowCountries]
                            : countryGroup.countries
                        ).map(country => (
                            <span
                                key={country.code}
                                className="b-multi-select-field-item__country-chip"
                                title={country.name}
                            >
                                {country.code}
                            </span>
                        ))}
                        {hasOverflow && (
                            <button
                                type="button"
                                className="b-multi-select-field-item__country-chip b-multi-select-field-item__country-chip-toggle"
                                onClick={toggleExpanded}
                            >
                                {isExpanded
                                    ? t('show_less')
                                    : `+${countryGroup.overflowCountries.length}`}
                            </button>
                        )}
                    </div>
                ) : (
                    <label>{label}</label>
                )}
                <ImageCheckbox check={selected} />
            </div>
        );
    }
);

export type DropdownOptionType = {
    label: string;
    value: any;
    isDisabled?: boolean;
    countryGroup?: DropdownOptionCountryGroup;
};

export type MultiSelectDropdownHintVisibility = 'always' | 'focused';

export type MultiSelectDropdownProps = {
    className?: string;
    defaultValue?: DropdownOptionType[];
    disabled?: boolean;
    emptyOptionsText?: string;
    hint?: React.ReactNode;
    hintVisibility?: MultiSelectDropdownHintVisibility;
    inputValue?: string;
    isFilterable?: boolean;
    isLoading?: boolean;
    isSingleSelect?: boolean;
    label: string;
    maxOptionsLength?: number;
    name?: string;
    onChange: (param: DropdownOptionType[]) => void;
    onQueryChange?: (param) => void;
    options: DropdownOptionType[];
    searchPlaceholder?: string;
    withSelectAllButton?: boolean;
};

const MultiSelectDropdown = ({
    className,
    defaultValue,
    disabled = false,
    emptyOptionsText,
    hint,
    hintVisibility = 'always',
    inputValue,
    isFilterable = false,
    isLoading = false,
    isSingleSelect,
    label,
    maxOptionsLength = OPTIONS_LENGTH_LIMIT,
    name,
    onChange,
    onQueryChange,
    options,
    searchPlaceholder,
    withSelectAllButton = false
}: MultiSelectDropdownProps) => {
    const { t } = useTranslation();

    const [isOpen, setIsOpen] = useState(false);
    const [filterQuery, setFilterQuery] = useState('');
    const [filteredOptions, setFilteredOptions] = useState<
        MultiSelectDropdownProps['options']
    >([]);
    const [selectedOptions, setSelectedOptions] = useState(defaultValue || []);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const firstOptionRef = useRef<HTMLDivElement>(null);

    const handleClick = e => {
        if (dropdownRef.current && dropdownRef.current.contains(e.target)) {
            // inside click
            return;
        }
        // outside click
        setIsOpen(false);
    };

    useEffect(() => {
        document.addEventListener('mousedown', handleClick);

        return () => {
            document.removeEventListener('mousedown', handleClick);
        };
    }, []);

    useEffect(() => {
        setFilteredOptions(
            options.length > OPTIONS_LENGTH_LIMIT
                ? options.slice(0, OPTIONS_LENGTH_LIMIT)
                : options
        );
    }, [options]);

    const toggleMenu = event => {
        if (event.target.title === 'tag-list-show-all-button') return;

        if (!disabled) {
            setIsOpen(!isOpen);
        }
    };

    const isSelected = option =>
        selectedOptions.findIndex(val => val.value === option.value) > -1;

    const handleSingleSelection = (option: DropdownOptionType) => {
        let nextSelected = [...selectedOptions];

        if (isSelected(option)) {
            nextSelected = nextSelected.filter(
                val => val.value !== option.value
            );
        } else if (isSingleSelect) {
            nextSelected = [option];
            setIsOpen(false);
        } else {
            nextSelected.push(option);
        }

        setSelectedOptions(nextSelected);
        onChange(nextSelected);
    };

    const selectAllOptions = () => {
        setSelectedOptions(options);
        onChange(options);
    };

    const deselectAllOptions = () => {
        setSelectedOptions([]);
        onChange([]);
    };

    const handleFiltering = (event: React.ChangeEvent<HTMLInputElement>) => {
        /**
         * We do not allow changing the query if async request is on-flight
         */
        if (isLoading) return;

        event.persist();

        const query = event.target.value;

        setFilterQuery(query);


        /**
         * If onQueryChange prop is defined, we exit this function execution,
         * and let parent component handle filtering. For example, async searching.
         */
        if (onQueryChange) {
            return onQueryChange(event.target.value);
        }

        const titleMatchesQuery = option =>
            new RegExp(query.toLowerCase()).test(option.label.toLowerCase());
        const matches = options.filter(titleMatchesQuery);

        if (matches.length > maxOptionsLength) {
            return setFilteredOptions(options.slice(0, maxOptionsLength));
        }

        if (!query) {
            return setFilteredOptions(options.slice(0, maxOptionsLength));
        }

        return debounce(
            () => setFilteredOptions(options.filter(titleMatchesQuery)) as any,
            200
        )();
    };

    const isAllSelected = () => options.length === selectedOptions.length;

    const menuClasses = classNames({
        'b-multi-select-menu': true,
        'is-visible': isOpen
    });

    const handleUnselecting = (optionValue: string) => event => {
        event.stopPropagation();
        const nextSelected = filter(propNotEq(optionValue, 'value'))(
            selectedOptions
        );
        setSelectedOptions(nextSelected);
        onChange(nextSelected);
    };

    return (
        <div
            className={classNames(
                {
                    'b-multi-select-field': true,
                    'is-focused': isOpen
                },
                className
            )}
            ref={dropdownRef}
            title={name}
            data-testid={name}
        >
            <div
                className="b-multi-select-field__input-wrapper"
                onClick={toggleMenu}
            >
                <div
                    className="b-multi-select-field__input-row"
                    style={{
                        width: `${dropdownRef.current?.offsetWidth}px`
                    }}
                >
                    <span
                        className={classNames('b-multi-select-field__label', {
                            'is-dirty': isNotEmpty(selectedOptions)
                        })}
                    >
                        {label}
                    </span>
                    {isNotEmpty(selectedOptions) && (
                        <div className="selected-options-container">
                            <TagList>
                                {selectedOptions.map(option => (
                                    <TagList.Tag
                                        name={option.label}
                                        key={option.value}
                                        className="selected-option-tag"
                                        title={option.label}
                                        onClick={handleUnselecting(
                                            option.value
                                        )}
                                    />
                                ))}
                            </TagList>
                        </div>
                    )}
                    <img
                        className="caret-down-icon"
                        src={caretIconPath}
                        onClick={toggleMenu}
                    />
                </div>
                <div
                    style={{
                        width: '100%',
                        margin: '8px 0',
                        opacity: '0.3',
                        height: '1px',
                        backgroundColor: '#000',
                    }}
                />
            </div>
            {isOpen && (
                <div className={classNames("b-multi-select-options-container", { "is-loading": isLoading })}>
                    <div className="b-multi-select-options-container__search-row">
                        <InputField
                            disabled={isLoading}
                            inputType="text"
                            isFocusDisabled
                            label={searchPlaceholder || label}
                            name={name || label }
                            onChangeHandler={isFilterable ? handleFiltering : undefined}
                            value={filterQuery || inputValue}
                            autoFocus
                            rightIcon={
                                <img
                                    className="b-multi-select-container__search-icon"
                                    src={searchIconPath}
                                    alt="search icon"
                                />
                            }
                        />
                    </div>
                    <div className={menuClasses}>
                        {isLoading && <Loader />}
                        {withSelectAllButton &&
                            options.length < OPTIONS_LENGTH_LIMIT && (
                                <ImageCheckbox
                                    className="select-all-button"
                                    value="false"
                                    key="selected_from_playlist"
                                    label={t('select_all')}
                                    check={isAllSelected()}
                                    onCheck={
                                        !isAllSelected()
                                            ? selectAllOptions
                                            : deselectAllOptions
                                    }
                                />
                            )}
                        {filteredOptions.map((option, index) => (
                            <MultiSelectItem
                                ref={index === 0 ? firstOptionRef : null}
                                key={option.value}
                                label={option.label}
                                selected={isSelected(option)}
                                isLoading={isLoading}
                                disabled={option.isDisabled}
                                countryGroup={option.countryGroup}
                                onClick={() => handleSingleSelection(option)}
                            />
                        ))}
                        {filteredOptions.length >= maxOptionsLength && (
                            <span className="options-length-warning">
                                Displaying first {maxOptionsLength} options. Use
                                search to narrow down options.
                            </span>
                        )}
                        {isLoading && filteredOptions.length === 0 && (
                            <span className="loading-options-text">
                                {t('multi_select_dropdown_loading_options')}
                            </span>
                        )}
                        {!isLoading && filteredOptions.length === 0 && (
                            <span className="empty-options-text">
                                {emptyOptionsText ||
                                    t('multi_select_dropdown_empty_options')}
                            </span>
                        )}
                    </div>
                </div>
            )}
            {hint && (hintVisibility === 'always' || isOpen) && (
                <div className="b-multi-select-field__hint">{hint}</div>
            )}
        </div>
    );
};
export default MultiSelectDropdown;
